import React from 'react';
import { Button, Checkbox, DateRangePicker, Dialog, DialogContent, DialogFooter, DialogTrigger, MoreFiltersButton, MultiSelectComboBox, type MultiSelectOption, type SelectOption } from '@chatsdet/ui';
import type { IssueSeverity, IssueStatus } from '@chatsdet/types/issues';
import type { DateRangeValue } from '../../../utils/dateRange';
import { formatDateRangeDisplay } from '../../../utils/dateRange';
import { SuperadminOnly } from '../SuperadminOnly';

export const STATUS_OPTIONS: MultiSelectOption[] = [
  { id: 'open', label: 'Open' },
  { id: 'closed', label: 'Closed' },
  { id: 'not_a_bug', label: 'Not a Bug' },
];

const STATUS_LABELS: Record<IssueStatus, string> = {
  open: 'Open',
  closed: 'Closed',
  not_a_bug: 'Not a Bug',
};

/** Inline single-select options — no 'all' entry; an empty selection means all. */
export const SEVERITY_OPTIONS: MultiSelectOption[] = [
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
  { id: 'unknown', label: 'Unknown' },
];

const SEVERITY_LABELS: Record<IssueSeverity | 'all', string> = {
  all: 'All Severities',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  unknown: 'Unknown',
};

export type IssuesFiltersSearchParams = {
  statuses?: IssueStatus[];
  severity?: IssueSeverity | 'all';
  propertyId?: string | 'all';
  categoryIds?: string[];
  credentialIds?: string[];
  tagIds?: string[];
  lastSeenStart?: string;
  lastSeenEnd?: string;
  includeArchived?: boolean;
  page?: number;
};

type IssuesFiltersProps = {
  statusFilter: IssueStatus[];
  severityFilter: IssueSeverity | 'all';
  propertyFilter: string;
  categoryFilter: string[];
  credentialFilter: string[];
  tagFilter: string[];
  showArchived: boolean;
  lastSeenRange: DateRangeValue;

  propertyOptions: SelectOption[];
  propertyNameMap: Record<string, string>;
  categoryNameMap: Record<string, string>;
  credentials: Array<{ id: string; name: string }>;
  credentialNameMap: Record<string, string>;
  tags: Array<{ id: string; name: string; color: string }>;
  tagNameMap: Record<string, string>;
  credentialsLoading?: boolean;
  tagsLoading?: boolean;

  onFilterChange: (updates: Partial<IssuesFiltersSearchParams>) => void;
  onLastSeenChange: (range: DateRangeValue) => void;
  onClearLastSeen: () => void;
  onCategoryToggle: (categoryId: string, selected: boolean) => void;

  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function IssuesFilters({
  statusFilter,
  severityFilter,
  propertyFilter,
  categoryFilter,
  credentialFilter,
  tagFilter,
  showArchived,
  lastSeenRange,
  propertyOptions: _propertyOptions,
  propertyNameMap,
  categoryNameMap,
  credentials,
  credentialNameMap,
  tags,
  tagNameMap,
  credentialsLoading,
  tagsLoading,
  onFilterChange,
  onLastSeenChange,
  onClearLastSeen,
  onCategoryToggle: _onCategoryToggle,
  isOpen,
  onOpenChange,
}: IssuesFiltersProps) {
  const isLastSeenInvalid = React.useMemo(() => {
    if (!lastSeenRange?.start || !lastSeenRange?.end) return false;
    return lastSeenRange.start.compare(lastSeenRange.end) > 0;
  }, [lastSeenRange]);

  const dateActive = Boolean(formatDateRangeDisplay(lastSeenRange));

  const activeFilterChips = React.useMemo(() => {
    const chips: Array<{ id: string; label: string }> = [];
    // Don't show status chips when 'all' sentinel is present (means user cleared the filter)
    if (statusFilter.length > 0 && !(statusFilter as string[]).includes('all')) {
      statusFilter.forEach((status) => {
        chips.push({ id: `status:${status}`, label: `Status: ${STATUS_LABELS[status]}` });
      });
    }
    if (severityFilter !== 'all') {
      chips.push({ id: 'severity', label: `Severity: ${SEVERITY_LABELS[severityFilter]}` });
    }
    if (propertyFilter !== 'all') {
      const label = propertyNameMap[propertyFilter] ?? propertyFilter;
      chips.push({ id: 'property', label: `Application: ${label}` });
    }
    if (categoryFilter.length > 0) {
      categoryFilter.forEach((categoryId) => {
        const label = categoryNameMap[categoryId] ?? categoryId;
        chips.push({ id: `category:${categoryId}`, label: `Category: ${label}` });
      });
    }
    const dateLabel = formatDateRangeDisplay(lastSeenRange);
    if (dateLabel) {
      chips.push({ id: 'lastSeen', label: `Last seen: ${dateLabel}` });
    }
    if (credentialFilter.length > 0) {
      credentialFilter.forEach((credId) => {
        const label = credentialNameMap[credId] ?? credId;
        chips.push({ id: `credential:${credId}`, label: `Credential: ${label}` });
      });
    }
    if (tagFilter.length > 0) {
      tagFilter.forEach((tagId) => {
        const label = tagNameMap[tagId] ?? tagId;
        chips.push({ id: `tag:${tagId}`, label: `Tag: ${label}` });
      });
    }
    if (showArchived) {
      chips.push({ id: 'archived', label: 'Show archived' });
    }
    return chips;
  }, [
    categoryFilter,
    categoryNameMap,
    credentialFilter,
    credentialNameMap,
    lastSeenRange,
    propertyFilter,
    propertyNameMap,
    severityFilter,
    showArchived,
    statusFilter,
    tagFilter,
    tagNameMap,
  ]);

  const filtersCount = activeFilterChips.length;
  const hasActiveFilters = filtersCount > 0;

  const handleFilterRemove = React.useCallback(
    (chipId: string) => {
      switch (chipId) {
        case 'severity':
          onFilterChange({ severity: 'all', page: 1 });
          break;
        case 'property':
          onFilterChange({ propertyId: 'all', page: 1 });
          break;
        case 'lastSeen':
          onClearLastSeen();
          break;
        case 'archived':
          onFilterChange({ includeArchived: undefined, page: 1 });
          break;
        default:
          if (chipId.startsWith('status:')) {
            const status = chipId.split(':', 2)[1] as IssueStatus;
            const nextStatuses = statusFilter.filter((s) => s !== status);
            onFilterChange({
              // Use 'all' sentinel when clearing all statuses so URL preserves the cleared state
              statuses:
                nextStatuses.length > 0 ? nextStatuses : (['all'] as unknown as IssueStatus[]),
              page: 1,
            });
          } else if (chipId.startsWith('category:')) {
            const categoryId = chipId.split(':', 2)[1];
            const nextIds = categoryFilter.filter((id) => id !== categoryId);
            onFilterChange({
              categoryIds: nextIds.length > 0 ? nextIds : undefined,
              page: 1,
            });
          } else if (chipId.startsWith('credential:')) {
            const credId = chipId.split(':', 2)[1];
            const nextIds = credentialFilter.filter((id) => id !== credId);
            onFilterChange({
              credentialIds: nextIds.length > 0 ? nextIds : undefined,
              page: 1,
            });
          } else if (chipId.startsWith('tag:')) {
            const tagId = chipId.split(':', 2)[1];
            const nextIds = tagFilter.filter((id) => id !== tagId);
            onFilterChange({
              tagIds: nextIds.length > 0 ? nextIds : undefined,
              page: 1,
            });
          }
          break;
      }
    },
    [categoryFilter, credentialFilter, onClearLastSeen, onFilterChange, statusFilter, tagFilter]
  );

  const handleResetFilters = React.useCallback(() => {
    onFilterChange({
      statuses: ['open'],
      severity: 'all',
      propertyId: 'all',
      categoryIds: undefined,
      credentialIds: undefined,
      tagIds: undefined,
      includeArchived: undefined,
      page: 1,
    });
    onClearLastSeen();
  }, [onClearLastSeen, onFilterChange]);

  const credentialOptions: MultiSelectOption[] = React.useMemo(
    () => credentials.map((c) => ({ id: c.id, label: c.name })),
    [credentials]
  );

  const selectedCredentials: MultiSelectOption[] = React.useMemo(
    () =>
      credentialFilter.map((id) => ({
        id,
        label: credentialNameMap[id] ?? id,
      })),
    [credentialFilter, credentialNameMap]
  );

  const handleCredentialChange = React.useCallback(
    (items: MultiSelectOption[]) => {
      onFilterChange({
        credentialIds: items.length > 0 ? items.map((i) => i.id) : undefined,
        page: 1,
      });
    },
    [onFilterChange]
  );

  const tagOptions: MultiSelectOption[] = React.useMemo(
    () => tags.map((t) => ({ id: t.id, label: t.name })),
    [tags]
  );

  const selectedTags: MultiSelectOption[] = React.useMemo(
    () =>
      tagFilter.map((id) => ({
        id,
        label: tagNameMap[id] ?? id,
      })),
    [tagFilter, tagNameMap]
  );

  const handleTagChange = React.useCallback(
    (items: MultiSelectOption[]) => {
      onFilterChange({
        tagIds: items.length > 0 ? items.map((i) => i.id) : undefined,
        page: 1,
      });
    },
    [onFilterChange]
  );

  // Count of active DIALOG-ONLY filters, shown on the "More" trigger. The
  // inline filters (status / severity / category) carry their own badges.
  const modalFiltersCount =
    credentialFilter.length + tagFilter.length + (dateActive ? 1 : 0) + (showArchived ? 1 : 0);

  const filterDialogTrigger = (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
      <MoreFiltersButton count={modalFiltersCount} />
      <Dialog
        title="More filters"
        description="Narrow down issues by recency, credential, or tag."
      >
        <DialogContent className="grid gap-4 md:grid-cols-2">
          <DateRangePicker
            label="Last seen"
            value={lastSeenRange}
            onChange={onLastSeenChange}
            granularity="day"
            className="md:col-span-2"
          />
          <MultiSelectComboBox
            label="Credentials"
            placeholder="Search credentials..."
            items={credentialOptions}
            value={selectedCredentials}
            onChange={handleCredentialChange}
            isLoading={credentialsLoading}
            className="md:col-span-2"
          />
          <MultiSelectComboBox
            label="Tags"
            placeholder="Search tags..."
            items={tagOptions}
            value={selectedTags}
            onChange={handleTagChange}
            isLoading={tagsLoading}
            className="md:col-span-2"
          />
          <SuperadminOnly className="md:col-span-2">
            <Checkbox
              isSelected={showArchived}
              onChange={(isSelected) => {
                onFilterChange({
                  includeArchived: isSelected ? true : undefined,
                  page: 1,
                });
              }}
              label="Include archived issues"
            />
          </SuperadminOnly>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={handleResetFilters}>
            Reset filters
          </Button>
          <Button
            variant="primary"
            onPress={() => onOpenChange(false)}
            isDisabled={isLastSeenInvalid}
          >
            Done
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );

  return {
    filterDialogTrigger,
    activeFilterChips,
    hasActiveFilters,
    filtersCount,
    handleFilterRemove,
    handleResetFilters,
  };
}
